/**
 * Build a date-keyed timeline of day-by-day items by merging:
 *   1. Any items the agent manually placed on a day (via Day-by-Day tab)
 *   2. Auto-derived items from top-level flights / hotels / tours / transportation
 *      based on their actual date fields.
 *
 * Each top-level item gets cloned into a synthetic DayItem on the matching day(s).
 * Already-paired ids prevent duplicate rendering when the agent has both a top-level
 * flight AND a manual day-by-day item for the same flight.
 *
 * Dedup keys (type-prefixed):
 *   "flt:<id>"   — flight (departure side)
 *   "flta:<id>"  — flight arrival echo
 *   "htl-in:<id>" / "htl-out:<id>"
 *   "tour:<id>"
 *   "xfer:<id>"
 *
 * Returns: Array<{ day_number, date, title, items[] }>
 */

function dateOnly(s) {
  if (!s) return "";
  // Accept "YYYY-MM-DD", "YYYY-MM-DDTHH:MM", or ISO datetime strings.
  const m = String(s).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

function timeOnly(s) {
  if (!s) return "";
  const m = String(s).match(/^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})/);
  return m ? m[1] : (String(s).match(/^(\d{1,2}:\d{2})/)?.[1] || "");
}

/**
 * Determine the effective sort time for a day item, falling back through the
 * type-specific time fields when the top-level `time` is empty. This lets a
 * Day-by-Day flight item (where the agent only filled in `departure_time`)
 * still sort correctly against a transfer keyed off `pickup_datetime`, etc.
 * Returns a zero-padded 24h string "HH:MM" for lexicographic comparison.
 */
function effectiveItemTime(item) {
  const candidates = [
    item.time,
    item.departure_time,
    item.check_in_time,
    item.check_out_time,
    timeOnly(item.pickup_datetime),
    timeOnly(item.departure),
    timeOnly(item.arrival),
    item.arrival_time,
  ];
  for (const c of candidates) {
    if (!c) continue;
    const m = String(c).match(/^(\d{1,2}):(\d{2})/);
    if (m) {
      return `${m[1].padStart(2, "0")}:${m[2]}`;
    }
  }
  return "99:99";
}

export function buildDayTimeline(itin) {
  if (!itin) return [];

  // Step 1 — establish the set of day buckets.
  // Prefer agent-defined `itin.days` when present; otherwise derive from start/end.
  const buckets = new Map(); // YYYY-MM-DD → day object
  const orderedDates = [];

  const ensureBucket = (dateISO, fallback = {}) => {
    if (!dateISO) return null;
    if (!buckets.has(dateISO)) {
      buckets.set(dateISO, {
        day_number: fallback.day_number || (orderedDates.length + 1),
        date: dateISO,
        title: fallback.title || "",
        items: [],
        _seen: new Set(),
      });
      orderedDates.push(dateISO);
    }
    return buckets.get(dateISO);
  };

  // Add an item to a date bucket, respecting dedup keys so the same item is
  // never rendered twice (e.g. when the agent has BOTH a top-level flight AND
  // a manual day-by-day flight for the same segment).
  const addItem = (dateISO, item, key) => {
    const b = ensureBucket(dateISO);
    if (!b) return;
    if (key && b._seen.has(key)) return;
    if (key) b._seen.add(key);
    b.items.push(item);
  };

  if (Array.isArray(itin.days) && itin.days.length > 0) {
    itin.days.forEach((d, i) => {
      const dateISO = dateOnly(d.date);
      if (!dateISO) return;
      // Skip days the agent has explicitly hidden from the client view.
      if (d.hidden) return;
      const b = ensureBucket(dateISO, { day_number: d.day_number || i + 1, title: d.title });
      // Mark this bucket as agent-explicit so it survives the empty-day cleanup.
      b._explicit = true;
      b.title = d.title || b.title;
      b.summary = d.summary || b.summary;
      b.cover_image_url = d.cover_image_url || b.cover_image_url;
      (d.items || []).forEach((item) => {
        // Critical: re-bucket typed items (flight/hotel/tour) onto their actual
        // date fields rather than blindly leaving them on the day the agent
        // happened to add them under. Falls back to the parent day's date when
        // no date field is set on the item.
        if (item.type === "flight") {
          const depD = dateOnly(item.departure_date) || dateISO;
          const depT = item.departure_time || timeOnly(item.departure_date);
          const arrD = dateOnly(item.arrival_date);
          const arrT = item.arrival_time || timeOnly(item.arrival_date);
          ensureBucket(depD)._explicit = ensureBucket(depD)._explicit || false;
          addItem(depD, { ...item, time: item.time || depT }, item.id ? `item:${item.id}` : null);
          if (arrD && arrD !== depD) {
            ensureBucket(arrD);
            addItem(arrD, {
              ...item,
              id: `${item.id || "flt"}__arr`,
              _echo: "arrival",
              time: arrT || "",
              departure_date: "",
              departure_time: "",
              arrival_date: arrD,
              arrival_time: arrT,
            }, item.id ? `flta:${item.id}` : null);
          }
          return;
        }
        if (item.type === "hotel") {
          const inD = dateOnly(item.check_in_date) || dateISO;
          const outD = dateOnly(item.check_out_date);
          ensureBucket(inD);
          addItem(inD, { ...item, time: item.time || item.check_in_time || "" }, item.id ? `item:${item.id}` : null);
          if (outD && outD !== inD) {
            ensureBucket(outD);
            addItem(outD, {
              ...item,
              id: `${item.id || "htl"}__out`,
              _echo: "checkout",
              time: item.check_out_time || "",
              check_in_date: "",
              check_in_time: "",
            }, item.id ? `htl-out:${item.id}` : null);
          }
          return;
        }
        // Other types stay on the day they were placed
        addItem(dateISO, { ...item }, item.id ? `item:${item.id}` : null);
      });
    });
  }

  // NOTE: we deliberately DO NOT auto-fill every date between start_date and
  // end_date with empty buckets. Empty days clutter the client view. Only
  // explicit agent days OR days that get populated by dated items below are
  // rendered. Empty buckets get pruned in the final step.

  // Step 2 — fold in top-level flights / hotels / tours / transportation as
  // synthetic day items based on their dates. Each item respects dedup keys
  // so we never render twice.

  // Flights: departure side on departure_date (legacy fallback to derived date),
  // arrival echo on arrival_date when distinct.
  (itin.flights || []).forEach((f) => {
    const depDate = dateOnly(f.departure_date) || dateOnly(f.departure);
    const depTime = f.departure_time || timeOnly(f.departure);
    const arrDate = dateOnly(f.arrival_date) || dateOnly(f.arrival);
    const arrTime = f.arrival_time || timeOnly(f.arrival);

    if (depDate) {
      addItem(depDate, {
        id: `flt-${f.id}`,
        type: "flight",
        time: depTime,
        title: "",
        airline: f.airline,
        flight_number: f.flight_number,
        cabin_class: f.cabin_class,
        from_airport: f.from_airport,
        to_airport: f.to_airport,
        departure_date: depDate,
        departure_time: depTime,
        arrival_date: arrDate,
        arrival_time: arrTime,
        confirmation_number: f.confirmation_number,
        supplier_contact: f.supplier_contact,
        price: f.price,
        currency: f.currency,
        link: f.link,
        notes: f.notes,
      }, `flt:${f.id}`);
    }

    if (arrDate && arrDate !== depDate) {
      addItem(arrDate, {
        id: `flt-${f.id}-arr`,
        type: "flight",
        _echo: "arrival",
        time: arrTime,
        title: "",
        airline: f.airline,
        flight_number: f.flight_number,
        cabin_class: f.cabin_class,
        from_airport: f.from_airport,
        to_airport: f.to_airport,
        // Hide the departure block on the arrival echo — emphasis is the landing.
        departure_date: "",
        departure_time: "",
        arrival_date: arrDate,
        arrival_time: arrTime,
        confirmation_number: f.confirmation_number,
        link: f.link,
      }, `flta:${f.id}`);
    }
  });

  // Hotels: check-in on check_in date (full hotel card), check-out on check_out
  // date as a clearly-labelled "Check-out" entry.
  (itin.hotels || []).forEach((h) => {
    const inDate = dateOnly(h.check_in);
    const outDate = dateOnly(h.check_out);
    if (inDate) {
      addItem(inDate, {
        id: `htl-${h.id}-in`,
        type: "hotel",
        time: h.check_in_time || "",
        title: "",
        hotel_name: h.name,
        address: h.address || h.location,
        location: h.location,
        room_type: h.room_type,
        check_in_date: inDate,
        check_in_time: h.check_in_time,
        check_out_date: outDate,
        check_out_time: h.check_out_time,
        confirmation_number: h.confirmation_number,
        supplier_contact: h.supplier_contact,
        price: h.price,
        currency: h.currency,
        link: h.link,
        image_url: h.image_url,
        notes: h.notes,
      }, `htl-in:${h.id}`);
    }
    if (outDate && outDate !== inDate) {
      addItem(outDate, {
        id: `htl-${h.id}-out`,
        type: "hotel",
        _echo: "checkout",
        time: h.check_out_time || "",
        title: "",
        hotel_name: h.name,
        address: h.address || h.location,
        location: h.location,
        room_type: h.room_type,
        // Show only the check-out side of the stay
        check_in_date: "",
        check_in_time: "",
        check_out_date: outDate,
        check_out_time: h.check_out_time,
        confirmation_number: h.confirmation_number,
        link: h.link,
      }, `htl-out:${h.id}`);
    }
  });

  // Tours: bucket on tour.date
  (itin.tours || []).forEach((t) => {
    const d = dateOnly(t.date);
    if (!d) return;
    addItem(d, {
      id: `tour-${t.id}`,
      type: "tour",
      time: timeOnly(t.date),
      title: t.title || t.provider || "Tour",
      location: t.location,
      description: t.description,
      duration: t.duration,
      image_url: t.image_url,
      confirmation_number: t.confirmation_number,
      supplier_contact: t.supplier_contact,
      price: t.price,
      currency: t.currency,
      link: t.link,
    }, `tour:${t.id}`);
  });

  // Transportation: bucket on pickup_datetime
  (itin.transportation || []).forEach((x) => {
    const d = dateOnly(x.pickup_datetime);
    if (!d) return;
    addItem(d, {
      id: `xfer-${x.id}`,
      type: "transportation",
      time: timeOnly(x.pickup_datetime),
      title: x.type ? `${x.type}` : "Transfer",
      location: `${x.pickup_location || ""}${x.dropoff_location ? ` → ${x.dropoff_location}` : ""}`.trim(),
      description: x.notes,
      confirmation_number: x.confirmation_number,
      supplier_contact: x.supplier_contact,
      provider: x.provider,
      vehicle: x.vehicle,
      price: x.price,
      currency: x.currency,
      link: x.link,
    }, `xfer:${x.id}`);
  });

  // Cruises: bucket departure + arrival
  (itin.cruises || []).forEach((c) => {
    const dep = dateOnly(c.departure);
    const arr = dateOnly(c.arrival);
    if (dep) {
      addItem(dep, {
        id: `cruise-${c.id}-dep`,
        type: "cruise",
        time: timeOnly(c.departure),
        title: c.line_name ? `${c.line_name}${c.ship_name ? ` · ${c.ship_name}` : ""}` : "Cruise embarkation",
        location: c.from_port,
        description: c.cabin_type ? `Cabin · ${c.cabin_type}` : "",
        image_url: c.image_url,
        confirmation_number: c.confirmation_number,
        supplier_contact: c.supplier_contact,
        price: c.price,
        currency: c.currency,
        link: c.link,
      }, `cruise-dep:${c.id}`);
    }
    if (arr && arr !== dep) {
      addItem(arr, {
        id: `cruise-${c.id}-arr`,
        type: "cruise",
        _echo: "arrival",
        time: timeOnly(c.arrival),
        title: c.line_name ? `Disembark · ${c.line_name}` : "Cruise arrival",
        location: c.to_port,
        link: c.link,
      }, `cruise-arr:${c.id}`);
    }
  });

  // Step 3 — Sort buckets chronologically, prune empty non-explicit buckets,
  // then assign each visible day its TRUE day_number — calendar offset from
  // the trip start_date + 1. Empty/hidden days don't compress numbering: if
  // Dec 1 is the start_date, Dec 8 is always "Day 8" even when Dec 3-7 are
  // pruned. This matches how travelers think about their trip ("we land on
  // day 5") rather than which slot the day occupies in the visible list.
  orderedDates.sort();
  const visibleDates = orderedDates.filter((d) => {
    const b = buckets.get(d);
    if (b._explicit) return true;
    return (b.items || []).length > 0;
  });

  const startISO = dateOnly(itin.start_date);
  const startMS = startISO ? Date.parse(`${startISO}T00:00:00Z`) : null;
  const dayOffset = (dateISO) => {
    if (!startMS) return null;
    const ms = Date.parse(`${dateISO}T00:00:00Z`);
    if (isNaN(ms)) return null;
    return Math.round((ms - startMS) / 86400000) + 1;
  };

  // Step 4 — sort each day's items by time and emit
  const out = visibleDates.map((d, i) => {
    const b = buckets.get(d);
    const trueDayNumber = dayOffset(d);
    // Use the calendar-based number when start_date is known; otherwise fall
    // back to visible position (1-based) so something sensible still shows.
    b.day_number = (trueDayNumber !== null && trueDayNumber > 0) ? trueDayNumber : (i + 1);
    delete b._seen;
    delete b._explicit;
    b.items.sort((a, x) => {
      const ta = effectiveItemTime(a);
      const tx = effectiveItemTime(x);
      return ta.localeCompare(tx);
    });
    return b;
  });

  return out;
}
